/**
 * @fileoverview `createNamespaceTranslate` — the ONE translator hook a defaults-bundle package builds from.
 * @package @puffin-core/i18n-bootstrap
 *
 * I18N-RENDER-002 Phase 0 (2026-08-20).
 *
 * ## What it replaces
 *
 * Five hand-rolled `use<Pkg>Translate` hooks that had drifted into **three** shapes:
 *
 * | hook | `chained` arity | `resolveString` call | interpolation |
 * | ---- | --------------- | -------------------- | ------------- |
 * | `useMfaTranslate`, `useReAuthTranslate` | `(key)` | 2-arg | unguarded |
 * | `useLoginTranslate` | `(key, params)` | 3-arg | gated on `params &&` |
 * | `useAuthShellTranslate` | `(key)` | 2-arg | **none** |
 *
 * The three are behaviourally equivalent once the interpolator's `onMissing` policy carries the
 * difference. `login-shell`'s `params &&` guard exists only to stop a `keep`-policy interpolator
 * running with no params — and a `keep` interpolator with no params is already a no-op, because
 * every placeholder is left intact. `auth-shell` omitted interpolation because its bundle has no
 * placeholders; its own docblock asks for `interpolate()` to be *"ported across at the same time"*
 * the day one is added, which this factory does for free.
 *
 * ## The resolution order, unchanged
 *
 *   1. the consumer's `t` prop — honoured FIRST, so a published component contract does not change;
 *   2. the package's i18next namespace, registered at boot by `@puffin-core/i18n-namespaces`;
 *   3. the package's English bundle — the degrade path, so an unwired tree renders readable English
 *      rather than a raw dotted key.
 *
 * There is no "no i18next instance" branch, deliberately: `useTranslation` ALWAYS returns a callable
 * `t`, and for an unregistered key that `t` returns THE KEY. `isUsableTranslation` treats the echoed
 * key as missing, which is what routes an unwired tree to step 3.
 *
 * ## 🔴 Why this is a SUBPATH, not a barrel export
 *
 * The barrel reaches `createAppI18n`. A package importing this from the barrel would pull the whole
 * i18n boot factory into its component graph. This module imports only React, `react-i18next` and the
 * React-free `./stringsKit.js`, and stays out of `index.js` for that reason. Its React-free half
 * (`createStringResolver`, `createInterpolator`) is reachable separately at `/strings-kit`, which is
 * what the leaf-only `strings/` subtree uses.
 *
 * ⚠️ **Hook-placement rule** for every hook built from this factory (AAS-I18N-002 RECIPE §3): call it
 * **immediately above the first computed hook that consumes it**. Higher and
 * `web/component-state-order` reports every translator-free `useCallback` beneath it; lower and you
 * ship a TDZ crash that lint scores 0 on.
 *
 * @module @puffin-core/i18n-bootstrap/createNamespaceTranslate
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { isUsableTranslation } from './stringsKit.js';

/**
 * Build a package's translation CHAIN as a pure function — the hook's own, for a render outside React
 * (STORE-SITES-CONVERGENCE-001 wave 1, R1 store: a server renderer builds a page's head before its tree
 * renders). `createNamespaceTranslate` is `useTranslation` + `useCallback` over exactly this, so the two
 * can never answer differently.
 *
 * @param {Object} config - as {@link createNamespaceTranslate}'s
 * @param {string} config.namespace
 * @param {(t: unknown, key: string, params?: Object) => string|undefined} config.resolveString
 * @param {(template: unknown, params?: unknown) => string} config.interpolate
 * @returns {((namespaceT: Function, consumerT?: Function) => (key: string, params?: Object) =>
 *   string|undefined) & {namespace: string}} The chain over a namespace `t` (an instance's
 *   `getFixedT(lng, namespace)`, or the hook's `useTranslation(namespace).t`), carrying its namespace.
 * @throws {TypeError} On any malformed field, naming it.
 */
export function createNamespaceTranslator({ namespace, resolveString, interpolate } = {}) {
  if (typeof namespace !== 'string' || namespace.length === 0) {
    throw new TypeError('createNamespaceTranslate: `namespace` must be a non-empty string');
  }
  if (typeof resolveString !== 'function') {
    throw new TypeError(
      `createNamespaceTranslate[${namespace}]: \`resolveString\` must be a function — build it with ` +
        "`createStringResolver(defaults)` so the English degrade path is the package's own bundle.",
    );
  }
  if (typeof interpolate !== 'function') {
    throw new TypeError(
      `createNamespaceTranslate[${namespace}]: \`interpolate\` must be a function — build it with ` +
        '`createInterpolator({braces, onMissing})` so the package keeps its own placeholder contract.',
    );
  }

  const translatorOf = (namespaceT, consumerT) => (key, params) => {
    // `resolveString` applies the usable-value check and the English fallback; this chain only
    // decides what it sees FIRST.
    //
    // ⚠️ `params` is FORWARDED to the consumer — a consumer `t` that assembles a whole sentence
    // from its params returns finished prose, which must not be re-interpolated away.
    //
    // ⚠️ And forwarded ONLY WHEN PRESENT: `consumerT(key, undefined)` is not the same call as
    // `consumerT(key)`, and sibling suites assert the exact arity with
    // `toHaveBeenCalledWith(key)`.
    const chained = (chainKey) => {
      if (typeof consumerT === 'function') {
        const fromConsumer =
          params !== undefined ? consumerT(chainKey, params) : consumerT(chainKey);
        if (isUsableTranslation(fromConsumer, chainKey)) return fromConsumer;
      }
      return params !== undefined ? namespaceT(chainKey, params) : namespaceT(chainKey);
    };

    // `params` reaches the resolver so its ENGLISH fallback can select a plural leaf by `count`
    // (`chained` closes over `params` and ignores the extra argument). Without it a plural key
    // resolved nothing on the degrade path and rendered blank.
    const resolved = resolveString(chained, key, params);
    return typeof resolved === 'string' ? interpolate(resolved, params) : resolved;
  };
  return Object.assign(translatorOf, { namespace });
}

/**
 * Build a package's `use<Pkg>Translate(consumerT)` hook.
 *
 * @param {Object} config
 * @param {string} config.namespace The i18next namespace the package registers under, from its
 *   zero-import `strings/namespace.js` leaf.
 * @param {(t: unknown, key: string, params?: Object) => string|undefined} config.resolveString The
 *   package's resolver, from `createStringResolver(defaults)`.
 * @param {(template: unknown, params?: unknown) => string} config.interpolate The package's
 *   interpolator, from `createInterpolator({braces, onMissing})`.
 * @returns {(consumerT?: Function) => (key: string, params?: Object) => string|undefined} The hook.
 * @throws {TypeError} On any malformed field, naming it.
 */
export function createNamespaceTranslate(config) {
  const translatorOf = createNamespaceTranslator(config);

  return function useNamespaceTranslate(consumerT) {
    const { t: namespaceT } = useTranslation(translatorOf.namespace);
    return useCallback(
      (key, params) => translatorOf(namespaceT, consumerT)(key, params),
      [consumerT, namespaceT],
    );
  };
}

export default createNamespaceTranslate;
